// import global variables.
import {randomString} from '../utils/global';
import {getTagModifier, getLimitedTag} from '../utils/createProductListUtils';
import {Modal} from './modal';
import {ChangeCartButton} from './changeCartButton';
import {Slider} from './slider';
import {Sort} from './sort';
import {updateAllButtonState} from './favoriteButton';
import favoriteItemsFilter from './favoriteItemsFilter';

/**
 * url - カートURL
 * @type {object}
 */
let url = {};

/**
 * modalContents - モーダルコンテンツ用マークアップを格納する配列
 * @type {Array}
 */
const modalContents = [];

/**
 * createAddCartButton - カートに追加ボタンのマークアップを生成
 *
 * @param  {"now" | "before" | "after" | "sold"}  saleStatus 販売状況
 * @param  {Boolean} hasModalContent              カートボタンがモーダルコンテンツを所持するか否か
 * @param  {String}  idName                       商品またはモーダルコンテンツのid名
 * @param  {Boolean}  isRedirect                  商品ページへリダイレクトを行うかどうか
 * @param  {String}  redirectText                 商品ページへリダイレクトを行うボタンのテキスト
 * @param  {String}  productUrl                   商品ページのURL
 * @return {String}                               カートに追加ボタンのマークアップ
 */
const createAddCartButton = (saleStatus, hasModalContent, idName, isRedirect = false, redirectText = '', productUrl = '') => {
    switch (saleStatus) {
    // 販売開始前
    case 'before':
        return '<a class="c-button-notAdd c-button-notAdd--thin"><span class="c-button-notAdd__label">販売開始前</span></a>';

    // 販売中
    case 'now':
    {
        let anchorElementStartTag = '';

        // リダイレクトさせる場合は、商品ページURLに遷移を行う
        if (isRedirect) {
            anchorElementStartTag = `<a class="c-button-addCart c-button-addCart--thin" href="${productUrl}" rel="noopener" target="_blank">`;
        // モーダルコンテンツを所持する場合、モーダル起動リンクとしてマークアップ
        } else if (hasModalContent) {
            anchorElementStartTag = `<a href="#${idName}" class="c-button-addCart c-button-addCart--thin js-modal">`;
        } else {
            anchorElementStartTag = `<a class="c-button-addCart c-button-addCart--thin" href="${url.linkUrl}${idName}${url.linkParam}" rel="noopener" target="_blank">`;
        }

        return `${anchorElementStartTag}<svg class="c-button-addCart__icon" height="22" width="23"><use xlink:href="#icon-addCart"></use></svg><span class="c-button-addCart__label">${isRedirect ? redirectText : 'カートに追加'}</span></a>`;
    }

    // 完売
    case 'sold':
        return '<a class="c-button-notAdd c-button-notAdd--thin"></svg><span class="c-button-notAdd__label">完売しました</span></a>';

    // 販売終了
    default:
        return '<a class="c-button-notAdd c-button-notAdd--thin"></svg><span class="c-button-notAdd__label">販売終了</span></a>';
    }
};

/**
 * createAmenitieModalContent - アメニティ（外付け特典）のモーダルコンテンツのマークアップを生成
 * @param  {Object} amenities            アメニティ情報を格納するオブジェクト
 * @param  {String} amenitiesModalIdName モーダルコンテンツのid名
 * @return {String}                      モーダルコンテンツのマークアップ
 */
const createAmenitieModalContent = (amenities, amenitiesModalIdName) => {
    const amenityOptions = [];

    // amenities.selectionの数だけ`option`を生成
    for (const selection of amenities.selection) {
        amenityOptions.push(`<option value="${selection.id}"${selection.id === '' ? ' disabled' : ''}>${selection.text}</option>`);
    }

    // モーダルコンテンツのマークアップを返却
    return `<div id="${amenitiesModalIdName}" class="p-modal">
    <div class="p-modal__content">
        <div class="p-text u-textCenter"><p class="c-text-lead js-modal__label">${amenities.title}</p></div>
        <div class="p-layout-column p-layout-column--center">
            <div class="p-layout-column__inner">
                <form method="POST" action="${url.postUrl}" name="${amenitiesModalIdName}" target="_blank" class="p-layout-column__item p-layout-column__item--fix">
                    <div class="p-text-tag"><p class="c-text-tag c-text-tag--wideFull">${amenities.type}</p></div>
                    <div class="p-form-select p-form-select--closely">
                        <select class="c-form-select" name="ADD_BNG" title="${amenities.type}">
                            <option value="" selected>お選びください</option>
                            ${amenityOptions.join('')}
                        </select>
                    </div>
                    <div class="p-button p-button--center">
                        <button type="submit" class="c-button-addCart c-button-addCart--full" onclick="if(document['${amenitiesModalIdName}']['ADD_BNG'].value === ''){event.stopImmediatePropagation();alert('選択してください。');return false;}"><svg focusable="false" class="c-button-addCart__icon" width="23" height="22"><use xlink:href="#icon-addCart"></use></svg><span class="c-button-addCart__label">カートに追加</span></button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</div>`;
};

/**
 * createSizeModalContent - サイズ選択のモーダルコンテンツのマークアップを生成
 *
 * @param {object} productData     商品の情報が格納されたオブジェクト
 * @param {string} modalIdName     モーダルコンテンツのid名
 * @param {string | undefined} amenitiesModalIdName アメニティモーダルコンテンツのid名
 * @return {string}                モーダルコンテンツのマークアップ
 */
const createSizeModalContent = (productData, modalIdName, amenitiesModalIdName) => {
    const sizeOptions = [];
    const modalButtons = [];
    const hasAmenities = typeof amenitiesModalIdName !== 'undefined';

    // サイズの数だけ`option`を生成（特典が付いている場合は`button`も生成）
    productData.items.forEach((item, index) => {
        switch (item.status) {
        case 'sold':
            // 商品が完売している場合
            sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''} data-soldout="true">${item.text}（完売しました）</option>`);

            return;
        case 'after':
            // 商品が販売終了の場合
            sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''} data-close="after">${item.text}（販売終了）</option>`);

            return;
        case 'before':
            // 商品が販売開始前の場合
            sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''} data-close="before">${item.text}（販売開始前）</option>`);

            return;
        default:
            break;
        }

        // 特典が付いている場合
        if (hasAmenities) {
            // 特定のサイズのみ特典がない場合を考慮
            if (item.amenities.selection.length === 0) {
                sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''}>${item.text}</option>`);

                return;
            }

            sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''} data-modal-id="${amenitiesModalIdName}-${index}">${item.text}</option>`);
            modalButtons.push(`<a href="#${amenitiesModalIdName}-${index}" class="c-button-addCart c-button-addCart--full js-change-cart-button__button js-modal" data-button-type="modal" hidden><svg focusable="false" class="c-button-addCart__icon" width="23" height="22"><use xlink:href="#icon-addCart"></use></svg><span class="c-button-addCart__label">${item.text}の特典を選択</span></a>`);

        // 特典が付いていない場合
        } else {
            sizeOptions.push(`<option value="${item.id}"${item.id === '' ? ' disabled' : ''}>${item.text}</option>`);
        }
    });

    // モーダルコンテンツのマークアップを返却
    return `<div id="${modalIdName}" class="p-modal">
        <div class="p-modal__content">
            <div class="p-text u-textCenter"><p class="c-text-lead js-modal__label">${productData.title}</p></div>
            <div class="p-layout-column p-layout-column--center">
                <div class="p-layout-column__inner">
                    <form method="POST" action="${url.postUrl}" name="${modalIdName}" target="_blank" class="p-layout-column__item p-layout-column__item--fix js-change-cart-button">
                        <div class="p-text-tag"><p class="c-text-tag c-text-tag--wideFull">${productData.optionsLabel}</p></div>
                        <div class="p-form-select p-form-select--closely">
                            <select class="c-form-select js-change-cart-button__select" name="ADD_BNG" title="${productData.optionsLabel}">
                                <option value="" selected>お選びください</option>
                                ${sizeOptions.join('')}
                            </select>
                        </div>
                        <div class="p-button p-button--center">
                            <button type="submit" class="c-button-addCart c-button-addCart--full js-change-cart-button__button" data-button-type="add-cart" onclick="if(document['${modalIdName}']['ADD_BNG'].value === ''){event.stopImmediatePropagation();alert('選択してください。');return false;}"><svg focusable="false" class="c-button-addCart__icon" width="23" height="22"><use xlink:href="#icon-addCart"></use></svg><span class="c-button-addCart__label">カートに追加</span></button>
                            <a class="c-button-notAdd c-button-notAdd--thin js-change-cart-button__button" data-button-type="sold" hidden><span class="c-button-notAdd__label">完売しました</span></a>
                            ${modalButtons.join('')}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>`;
};

/**
 * createModalContent - モーダルコンテンツのマークアップを生成
 * @param  {Object} items                タイトル製品タイトルがもつ、製品の詳細情報が格納されたオブジェクト
 * @param  {String} modalIdName          モーダルコンテンツのid名
 * @param  {String} amenitiesModalIdName モーダルコンテンツ（外付け特典）のid名
 * @return {String}                      モーダルコンテンツのマークアップ
 */
const createModalContent = (items, modalIdName, amenitiesModalIdName) => {
    const hasAmenities = typeof amenitiesModalIdName !== 'undefined';
    const itemCategories = [];
    const columnContents = [];

    /**
     * createModalColumnContents - モーダル内のカラムコンテンツのマークアップを生成
     * @param  {Object} item            製品の詳細情報が格納されたオブジェクト
     * @param  {String} idName          モーダルコンテンツ（外付け特典）のid名
     * @return {String}                 モーダル内カラムコンテンツのマークアップ
     */
    const createModalColumnContents = (item, idName) => `<div class="p-layout-column__item p-layout-column__item--fix">
    <div class="p-text-tag"><p class="c-text-tag c-text-tag--full c-text-tag${getTagModifier(item.category)}">${item.category}</p></div>
    <div class="p-text"><p class="c-text-priceAttention"><span class="c-text-priceAttention__price">¥${item.price}</span><span class="c-text-priceAttention__tax">(税込)</span></p></div>
    <div class="p-button">${createAddCartButton(item.status, hasAmenities, hasAmenities ? idName : item.id)}</div>
</div>`;

    // 製品情報ごとにコンテンツを生成
    for (let i = 0; i < items.length; i++) {
        const amenitiesIdName = hasAmenities ? `${amenitiesModalIdName}-${i}` : '';
        const item = items[i];

        // アメニティ（外付け特典）を所持している場合、アメニティモーダルコンテンツを生成
        if (hasAmenities) {
            modalContents.push(createAmenitieModalContent(item.amenities, amenitiesIdName));
        }

        // カテゴリ情報を配列に追加
        itemCategories.push(item.category);

        // モーダル内カラムコンテンツを生成・配列に追加
        columnContents.push(createModalColumnContents(item, amenitiesIdName));
    }

    // モーダルコンテンツのマークアップを返却
    return `<div id="${modalIdName}" class="p-modal">
    <div class="p-modal__content">
        <div class="p-text u-textCenter">
            <p class="c-text-lead js-modal__description">こちらの商品は、${itemCategories.join('、')}がございます。いずれかを選択してください。</p>
        </div>
        <div class="p-layout-column p-layout-column--center">
            <div class="p-layout-column__inner">${columnContents.join('')}</div>
        </div>
    </div>
</div>`;
};

/**
 * createPriceList - 価格リストのマークアップを生成
 * @param  {Object} items 製品の詳細情報が格納されたオブジェクト
 * @return {String}       価格リストのマークアップ
 */
const createPriceList = (items) => {
    const priceListItem = [];


    // アイテムの数だけ価格表示を生成・配列に追加
    for (const item of items) {
        const category = item.category.match(/\(.*\)$/) ? item.category.replace(/\(.*\)$/, '') : item.category;

        priceListItem.push(`<li class="p-list-price__item"><div class="p-list-price__meta p-text-tag"><span class="c-text-tag c-text-tag--minfix">${category}</span><span class="c-text-price c-text-price--small"><span class="c-text-price__price">¥${item.price}</span><span class="c-text-price__tax">(税込)</span></span></div></li>`);
    }

    // マークアップを返却
    return `<div class="p-layout-product__price p-list-price p-list-price--thin">
    <ul class="p-list-price__list">
        ${priceListItem.join('')}
    </ul>
</div>`;
};

/**
 * createProductItems - 商品リストのアイテムのマークアップを生成
 * @param  {Object} productData  商品の情報が格納されたオブジェクト
 * @param  {String} templateType テンプレートの種類（favorite|recommend|cheked）
 * @return {String}              商品リストのアイテムのマークアップ
 */
const createProductItems = (productData, templateType) => {
    const hasMultipleItems = Boolean(productData.items.length > 1 && productData.withOptions === 'false');
    const hasMultipleSize = Boolean(productData.items.length > 1 && productData.withOptions === 'true');
    const isRedirect = productData.redirect === '1';
    const hasLimited = productData.limited !== '0';
    const hasAmenities = (() => {
        for (const item of productData.items) {
            if (item.amenities.selection.length !== 0) {
                return true;
            }
        }

        return false;
    })();

    const hasModalContent = hasMultipleItems || hasAmenities || hasMultipleSize;

    let modalIdName = '';

    // ステータスが販売中 かつ モーダルコンテンツを所持する場合
    if (productData.status === 'now' && hasModalContent) {
        modalIdName = randomString('modal-');

        // 複数商品・複数サイズを所持しているタイトル かつ 外付け特典も所持している場合
        if ((hasMultipleItems || hasMultipleSize) && hasAmenities) {
            const amenitiesModalIdName = `${modalIdName}-amenity`;

            // モーダルコンテンツを生成
            if (hasMultipleSize) {
                modalContents.push(createSizeModalContent(productData, modalIdName, amenitiesModalIdName));
            } else {
                modalContents.push(createModalContent(productData.items, modalIdName, amenitiesModalIdName));
            }

            // アイテムの数だけモーダルコンテンツ（外付け特典）を生成
            for (let i = 0; i < productData.items.length; i++) {
                if (productData.items[i].amenities.selection.length !== 0) {
                    modalContents.push(createAmenitieModalContent(productData.items[i].amenities, `${amenitiesModalIdName}-${i}`));
                }
            }

        // 複数コンテンツを所持しているだけの場合
        } else if (hasMultipleItems) {
            modalContents.push(createModalContent(productData.items, modalIdName));

        // 複数サイズを所持しているだけの場合
        } else if (hasMultipleSize) {
            modalContents.push(createSizeModalContent(productData, modalIdName));

        // 外付け特典を所持しているだけの場合
        } else if (hasAmenities) {
            modalContents.push(createAmenitieModalContent(productData.items[0].amenities, modalIdName));
        }
    }

    // 商品リストのアイテムのマークアップを返却
    return `<div class="p-layout-product__content">
    <p class="p-layout-product__categoryTag c-text-solidTag c-text-solidTag${getTagModifier(productData.category)}">${productData.category}</p>
    <a href="${productData.url}" class="p-layout-product__imageLink"${isRedirect ? 'target="_blank"' : ''}>
        <div class="c-media-imageObject"><img src="${productData.thumbnail}" alt="" loading="lazy" decoding="async" class="c-media-imageObject__item"></div>
        ${templateType === 'favorite' && productData.releaseText !== '' ? `<p class="p-layout-product__date">${productData.releaseText}</p>` : ''}
        ${productData.bto === 'true' ? '<div class="p-text-tag p-text-tag--bto"><p class="c-text-btoTag"><span class="c-text-btoTag__txt">受注生産商品</span></p></div>' : ''}
        <p class="p-layout-product__title"><b>${productData.title}</b></p>
    </a>
    ${hasLimited ? getLimitedTag(productData.limited) : ''}
</div>
<div class="p-layout-product__actionArea">
    ${hasMultipleItems ? createPriceList(productData.items) : `<p class="p-layout-product__price c-text-price c-text-price--small"><span class="c-text-price__price">¥${productData.items[0].price}</span><span class="c-text-price__tax">(税込)</span></p>`}
    <ul class="p-layout-product__button p-button-unit p-button-unit--thin">
        <li class="p-button-unit__item">${createAddCartButton(productData.status, hasModalContent, hasModalContent ? modalIdName : productData.items[0].id, isRedirect, productData.redirectText, productData.url)}</li>
        <li class="p-button-unit__item p-button-unit__item--fix"><button type="button" class="c-button-favorite c-button-favorite--thin" data-type="product" data-id="${productData.items[0].id}"><svg focusable="false" class="c-button-favorite__icon" width="30" height="22"><use xlink:href="#icon-addFavorite"></use></svg><span class="c-button-favorite__label u-altText">お気に入りに追加</span></button></li>
    </ul>
</div>`;
};

/**
 * createWorksItems - 作品または声優・アーティストのリストアイテムのマークアップを生成
 * @param  {Object} worksData 作品または声優・アーティストの情報が格納されたオブジェクト
 * @param  {String} itemType  worksDataの種類（work|artist）
 * @return {String}           リストアイテムのマークアップ
 */
const createWorksItems = (worksData, itemType) => `<div class="p-layout-product__content p-layout-product__content--withFavorite">
    <a href="${worksData.url}" class="p-layout-product__imageLink">
        <div class="c-media-imageObject"><img src="${worksData.thumbnail}" alt="" loading="lazy" decoding="async" class="c-media-imageObject__item"></div>
        <p class="p-layout-product__title"><b>${worksData.title}</b></p>
    </a>
    <div class="p-layout-product__button">
        <button type="button" class="c-button-favorite c-button-favorite--min" data-type="${itemType}" data-id="${worksData.id}">
            <svg focusable="false" class="c-button-favorite__icon" width="30" height="22"><use xlink:href="#icon-addFavorite"></use></svg>
            <span class="c-button-favorite__label u-altText">お気に入りに追加</span>
        </button>
    </div>
</div>`;

/**
 * デフォルト実行処理
 * @param  {HTMLElement} targetElement ルートとなる要素
 * @param  {String} linkageType   データ連携の種類（favorite|recommend|checked）
 * @param  {Object} data          商品情報が格納されたオブジェクト（APIから返却されたJSON）
 * @return {Void}
 */
export default (targetElement, linkageType, data) => {
    const isFavoriteType = linkageType === 'favorite';
    const dataKeys = Object.keys(data);
    const items = [];

    [url] = data.cart;

    // オブジェクトが空だった場合は終了
    if (!dataKeys.length) {
        // データの種類が「お気に入り」ではない場合、文言を表示する
        if (!isFavoriteType) {
            const hiddenAreaTarget = targetElement.closest('.js-hidden-area-target');

            // 祖先要素に対象クラスを持つ要素がいる場合、エリアごと非表示
            if (hiddenAreaTarget) {
                hiddenAreaTarget.outerHTML = '';

                return;
            }

            targetElement.parentNode.classList.add('is-scroll');
            targetElement.insertAdjacentHTML('beforeend', '<div class="p-text u-textCenter"><p class="c-text-weak">該当する商品が存在しません</p></div>');
        }

        return;
    }

    // 呼び出しごとに modalContents[] を空に
    modalContents.length = 0;

    // データの種類が「お気に入り」だった場合
    if (isFavoriteType) {
        for (const key of dataKeys) {
            const replacedKeyName = key.replace(/s$/, '');
            const isProducts = replacedKeyName === 'product';

            // アイテムごとにHTMLを生成/・配列に追加
            for (const item of data[key]) {
                items.push(`<li class="p-layout-column__item p-layout-product" data-filter-type="${replacedKeyName}">${isProducts ? createProductItems(item, linkageType) : createWorksItems(item, replacedKeyName)}</li>`);
            }
        }

    // データの種類が「お気に入り」以外の場合
    } else {
        // data.productsを所持していない場合は何もしない
        if (!data.hasOwnProperty('products')) {
            return;
        }

        // 商品の数だけHTMLを生成・配列に追加
        for (const product of data.products) {
            items.push(`<li class="p-list-product__item p-layout-product">${createProductItems(product, linkageType)}</li>`);
        }
    }

    // 生成したHTMLを追加
    targetElement.insertAdjacentHTML('beforeend', `<ul class="${isFavoriteType ? 'p-layout-column__inner p-layout-column__inner--5column p-layout-column__inner--narrow2column p-layout-column__inner--narrow js-favorite-filter__content js-sort__container' : 'p-list-product'}">${items.join('')}</ul>`);
    targetElement.insertAdjacentHTML('beforeend', modalContents.join(''));

    // データの種類が「お気に入り」だった場合、ソート・絞り込み機能を実行
    if (isFavoriteType) {
        document.querySelectorAll('.js-favorite-sort').forEach((element) => new Sort(element));
        favoriteItemsFilter();

    // データの種類が「お気に入り」以外の場合、スライダー機能を実行
    } else {
        [targetElement.parentNode].forEach((element) => new Slider(element));
    }

    // お気に入り追加ボタン・モーダル機能を実行
    updateAllButtonState(targetElement.querySelectorAll('.c-button-favorite'));
    targetElement.querySelectorAll('.js-modal').forEach((element) => new Modal(element));
    targetElement.querySelectorAll('.js-change-cart-button').forEach((element) => new ChangeCartButton(element));
};
